// Room translations (6 languages) — per-room editor + "AI translate all" bar for the Rooms admin tab.
import React, { useCallback, useEffect, useState } from 'react';
import { Languages, Loader2, Save, Sparkles } from 'lucide-react';
import { apiCall } from './_shared';
import { RoomCopyPreview } from './RoomCopyPreview';
import { CopyCheckButton, CopyCheckPanel, useCopyCheck } from './CopyCheck';

const LANGS = [['me', 'Crnogorski'], ['sq', 'Shqip'], ['de', 'Deutsch'], ['ru', 'Русский'], ['pl', 'Polski'], ['hu', 'Magyar']];

export const RoomTranslationsBar = ({ onDone }) => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => apiCall('/api/admin/rooms/translations/status').then(setSt).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!st?.job?.running) return undefined;
    const id = setInterval(() => { load(); }, 4000);
    return () => clearInterval(id);
  }, [st?.job?.running, load]);
  useEffect(() => { if (st && !st.job?.running && st.job?.finished_at) onDone?.(); }, [st?.job?.finished_at]); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async (overwrite) => {
    if (overwrite && !window.confirm('Re-translate ALL rooms and overwrite existing translations?')) return;
    setBusy(true);
    try { await apiCall(`/api/admin/rooms/translate-all?overwrite=${overwrite}`, { method: 'POST' }); await load(); } finally { setBusy(false); }
  };
  if (!st) return null;
  const job = st.job || {};
  return (
    <div className="bg-white border rounded-xl p-3 flex flex-wrap items-center gap-3 text-sm" data-testid="room-translations-bar">
      <Languages className="w-5 h-5 text-orange-500" />
      <div className="flex-1 min-w-[200px]">
        <div className="font-semibold text-slate-900" data-testid="room-translations-summary">Room content in 7 languages: {st.complete}/{st.total} rooms complete</div>
        <div className="text-xs text-slate-500">Names, descriptions and amenities shown to visitors in their language (SEO for local searches). English is the base text.</div>
        {job.running && <div className="text-xs text-orange-700 mt-1" data-testid="room-translations-progress">Translating… {job.done}/{job.total}{job.failed?.length ? ` · ${job.failed.length} failed` : ''}</div>}
        {!job.running && job.finished_at && <div className="text-xs text-emerald-700 mt-1">Last run finished {String(job.finished_at).slice(0, 16).replace('T', ' ')}{job.failed?.length ? ` · failed: ${job.failed.map((f) => `#${f.id}`).join(', ')}` : ''}</div>}
      </div>
      <button type="button" onClick={() => run(false)} disabled={busy || job.running} className="inline-flex items-center gap-1 bg-slate-900 text-white rounded px-3 py-1.5 text-xs font-semibold disabled:opacity-60" data-testid="room-translate-missing">
        {job.running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} AI translate missing
      </button>
      <button type="button" onClick={() => run(true)} disabled={busy || job.running} className="text-xs text-slate-600 hover:underline disabled:opacity-60" data-testid="room-translate-all">Re-translate all</button>
    </div>
  );
};

export const RoomTranslationsEditor = ({ room, onSaved }) => {
  const [tab, setTab] = useState('de');
  const [tr, setTr] = useState(room.translations || {});
  const [savedTr, setSavedTr] = useState(JSON.stringify(room.translations || {}));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  useEffect(() => { setTr(room.translations || {}); setSavedTr(JSON.stringify(room.translations || {})); }, [room]);
  const cur = tr[tab] || { name: '', description: '', amenities: null };
  const set = (k, v) => setTr({ ...tr, [tab]: { ...cur, [k]: v } });
  const check = useCopyCheck(`hotel room listing "${room.name}" (${room.size} m², ${room.guests} guests)`);
  const runRoomCheck = () => check.run([
    { key: 'description:en', lang: 'en', kind: 'room description', text: room.description || '' },
    ...LANGS.flatMap(([code]) => [
      { key: `name:${code}`, lang: code, kind: 'room name', text: tr[code]?.name || '' },
      { key: `description:${code}`, lang: code, kind: 'room description', text: tr[code]?.description || '' },
    ]),
  ]);
  const applyRewrite = async (r) => {
    const [field, code] = r.key.split(':');
    if (code === 'en') {
      if (!window.confirm('Replace the English description now? (English is saved immediately — it is the base text for all languages.)')) return;
      setBusy(true); setMsg('');
      try { await apiCall(`/api/admin/rooms/${room.id}`, { method: 'PUT', body: JSON.stringify({ description: r.rewrite }) }); setMsg('English description updated.'); onSaved?.(tr); }
      catch (e) { setMsg(e.message); } finally { setBusy(false); }
      return;
    }
    setTr((p) => ({ ...p, [code]: { ...(p[code] || {}), [field]: r.rewrite } }));
    setTab(code);
  };
  const labelFor = (r) => {
    const [field, code] = r.key.split(':');
    const l = LANGS.find(([c]) => c === code);
    return `${field === 'name' ? 'Name' : 'Description'} · ${code === 'en' ? 'English' : l ? l[1] : code}`;
  };
  const save = async () => {
    setBusy(true); setMsg('');
    try {
      const payload = Object.fromEntries(Object.entries(tr).filter(([, v]) => v?.name).map(([k, v]) => [k, { name: v.name, description: v.description || '', amenities: v.amenities || null }]));
      const r = await apiCall(`/api/admin/rooms/${room.id}/translations`, { method: 'PUT', body: JSON.stringify({ translations: payload }) });
      setTr(r.translations); setSavedTr(JSON.stringify(r.translations)); setMsg('Saved.'); onSaved?.(r.translations);
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };
  const ai = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await apiCall(`/api/admin/rooms/${room.id}/translate?overwrite=true`, { method: 'POST' });
      setTr(r.translations); setSavedTr(JSON.stringify(r.translations)); setMsg(`AI translated into ${r.translated.length} languages.`); onSaved?.(r.translations);
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };
  const tabs = LANGS.map(([code, label]) => (
    <button key={code} type="button" onClick={() => setTab(code)} className={`px-2.5 py-1 rounded-full text-xs font-semibold ${tab === code ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700'}`} data-testid={`room-tr-tab-${code}`}>
      {label}{tr[code]?.name ? ' ✓' : ''}
    </button>
  ));
  return (
    <div className="mt-3 border rounded-xl p-3 bg-slate-50 space-y-2" data-testid={`room-translations-${room.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-slate-600 mr-1">Translations</span>{tabs}
        <div className="ml-auto flex items-center gap-3">
          <CopyCheckButton busy={check.busy} onClick={runRoomCheck} testId={`room-tr-quality-${room.id}`} />
          <button type="button" onClick={ai} disabled={busy} className="inline-flex items-center gap-1 text-xs font-semibold text-orange-600 hover:underline disabled:opacity-60" data-testid="room-tr-ai">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} AI translate this room
          </button>
        </div>
      </div>
      <CopyCheckPanel check={check} labelFor={labelFor} onApply={applyRewrite} testPrefix={`room-copy-check-${room.id}`} />
      <p className="text-[11px] text-slate-500">EN: <b>{room.name}</b></p>
      <input value={cur.name || ''} onChange={(e) => set('name', e.target.value)} placeholder={`Room name (${tab})`} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="room-tr-name" />
      <textarea value={cur.description || ''} onChange={(e) => set('description', e.target.value)} placeholder={`Description (${tab})`} rows={3} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="room-tr-description" />
      <input value={(cur.amenities || []).join(', ')} onChange={(e) => set('amenities', e.target.value ? e.target.value.split(',').map((x) => x.trim()).filter(Boolean) : null)} placeholder={`Amenities, comma separated (${tab}) — leave empty to show English`} className="w-full border rounded px-2 py-1.5 text-xs" data-testid="room-tr-amenities" />
      <div className="flex items-center gap-3">
        <button type="button" onClick={save} disabled={busy} className="inline-flex items-center gap-1 bg-emerald-600 text-white rounded px-3 py-1.5 text-xs font-semibold disabled:opacity-60" data-testid="room-tr-save"><Save className="w-3 h-3" /> Save translations</button>
        {msg && <span className="text-xs text-slate-700" data-testid="room-tr-msg">{msg}</span>}
        {cur.source && <span className="text-[11px] text-slate-400">{cur.source === 'ai' ? 'AI-generated' : 'manual'} · {String(cur.updated_at || '').slice(0, 10)}</span>}
      </div>
      <RoomCopyPreview room={room} translations={tr} langs={LANGS} activeLang={tab} onSelect={setTab} dirty={JSON.stringify(tr) !== savedTr} />
    </div>
  );
};
